import React from "react";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import { EditTextFieldDialog } from "../core/EditTextFieldDialog";
import ContentCopy from "@mui/icons-material/ContentCopy";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import { enqueueSnackbar } from "notistack";
import { useCopyToClipboard } from "usehooks-ts";
import { ExternalLink } from "../styleguide/AppLink";
import { addUtmSource } from "../util/add-utm";

export const Buttons = ({ value }) => {
  const [, copy] = useCopyToClipboard();

  const copyUrlToClipboard = (e) => {
    if (!value) {
      return e.preventDefault();
    }

    copy(value);
    enqueueSnackbar("Url copied to clipboard", { variant: "info" });

    return e.preventDefault();
  };

  return (
    <>
      <ListItemIcon>
        <ListItemButton onClick={copyUrlToClipboard}>
          <ContentCopy />
        </ListItemButton>
      </ListItemIcon>
      <ExternalLink href={value}>
        <ListItemIcon>
          <ListItemButton>
            <OpenInNewOutlined />
          </ListItemButton>
        </ListItemIcon>
      </ExternalLink>
    </>
  );
};

const Display = ({ title, value }) => {
  return (
    <ListItemText
      primary={title}
      secondary={value || "click to edit"}
      sx={{
        maxWidth: "66ch",
        whiteSpace: "pre-wrap",
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 2, // Change this to your desired number of lines
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    />
  );
};

/**
 * @param {object} props
 * @param {string} props.title
 * @param {import("@seabrookstudios/types").Url|string|undefined} props.text
 * @param {string} props.helperText
 * @param {boolean} [props.disabled]
 * @param {(text: string) => string} [props.formatter]
 * @param {boolean} [props.multiline]
 * @param {boolean} [props.isBusy]
 * @param {(text: import("@seabrookstudios/types").Url|string) => void} props.onSave
 * @param {() => React.JSX.Element} [props.Icon]
 * @param {() => void} [props.onDelete]
 * @param {boolean} [props.noIcon]
 */
export const EditableListItemUrl = ({
  title,
  text,
  helperText,
  onSave,
  disabled,
  isBusy = false,
  multiline = true,
  formatter = (x) => x,
  Icon,
  onDelete,
  noIcon = false,
}) => {
  const hasUrl = text && text.length > 0;

  return (
    <>
      {disabled ? (
        <ListItem>
          {!noIcon && <ListItemIcon>{Icon && <Icon />}</ListItemIcon>}
          <Display title={title} value={formatter(text || "")} />
          {hasUrl && <Buttons value={formatter(text)} />}
        </ListItem>
      ) : (
        <ListItem>
          {!noIcon && <ListItemIcon>{Icon && <Icon />}</ListItemIcon>}
          <ListItemText>
            <EditTextFieldDialog
              onSave={onSave}
              title={title}
              initialValue={text}
              isBusy={isBusy}
              helperText={helperText}
              multiline={multiline}
              onDelete={onDelete}
            >
              <Display title={title} value={formatter(text || "")} />
            </EditTextFieldDialog>
          </ListItemText>
          {hasUrl && <Buttons value={formatter(text)} />}
        </ListItem>
      )}
    </>
  );
};

/**
 * @param {object} props
 * @param {string} props.title
 * @param {import("@seabrookstudios/types").Url|string} props.text
 * @param {() => React.JSX.Element} [props.Button]
 */
export const StaticListItemUrl = ({ title, text, Button }) => {
  const hasUrl = text && text.length > 0;

  if (!hasUrl) {
    return;
  }

  return (
    <Stack direction="column">
      <ListItem
        secondaryAction={
          <Stack direction="row">
            {hasUrl && <Buttons value={addUtmSource(text)} />}
            {Button && <Button />}
          </Stack>
        }
      >
        <ListItemText
          primary={title}
          secondary={
            <a href={addUtmSource(text || "")} target="_blank" rel="noreferrer">
              {text}
            </a>
          }
          sx={{ maxWidth: "66ch", whiteSpace: "pre-wrap" }}
        />
      </ListItem>
    </Stack>
  );
};
